import { motion } from 'framer-motion';
import { AlertTriangle, Rocket, X, Check, Factory, Cog, Zap, Wrench } from 'lucide-react';

const ProblemSolutionSection = () => {
  const targetAudience = [
    { icon: Factory, label: 'Industriekunden' },
    { icon: Cog, label: 'Sondermaschinenbau' },
    { icon: Wrench, label: 'Automatisierungstechnik' },
    { icon: Zap, label: 'Express-Fertigung' },
  ];

  const problems = [
    'Kurzfristige Produktionsengpässe',
    'Hoher Kostendruck in der Beschaffung',
    'Lange Wartezeiten auf Angebote',
    'Unflexible Lieferanten',
  ];

  const solutions = [
    { title: 'FastLane', desc: 'Hochqualifizierter Mitarbeiter meldet sich in Minuten' },
    { title: 'Express', desc: 'Fertigung oft noch am selben Tag' },
    { title: 'Smart Production', desc: 'Integration in laufende Projekte = minimale Kosten' },
    { title: 'Transparent', desc: 'Ein Ansprechpartner, maximale Rückverfolgbarkeit' },
  ];

  return (
    <section className="py-20 lg:py-32">
      <div className="container mx-auto px-4">
        {/* Target Audience Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <p className="text-muted-foreground text-sm uppercase tracking-wider mb-3">Spezialisiert auf</p>
          <div className="flex flex-wrap justify-center gap-3 md:gap-4">
            {targetAudience.map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.label}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="flex items-center gap-2 bg-primary/10 border border-primary/20 rounded-full px-4 py-2"
                >
                  <Icon className="w-4 h-4 text-primary" />
                  <span className="text-foreground font-medium text-sm md:text-base">{item.label}</span>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* Urgency Message */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="text-center mb-12"
        >
          <h2 className="font-heading font-bold text-2xl md:text-3xl lg:text-4xl text-foreground">
            Sie brauchen <span className="text-fastlane">schnell</span> präzise Teile?
          </h2>
          <p className="text-muted-foreground text-lg mt-2 max-w-2xl mx-auto">
            Ob Produktionsengpass, dringende Reparatur oder eiliger Prototyp – wir sind Ihr Partner für zeitkritische Fertigung.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-0 rounded-2xl overflow-hidden shadow-xl">
          {/* Problem Side */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-[#2a2a2a] p-8 lg:p-12"
          >
            <div className="flex items-center gap-3 mb-8">
              <div className="w-12 h-12 rounded-full bg-cyan-500/20 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-cyan-400" />
              </div>
              <h3 className="font-heading font-bold text-2xl lg:text-3xl text-white">
                Kennen Sie diese Situation?
              </h3>
            </div>

            <div className="space-y-6">
              {problems.map((problem, index) => (
                <motion.div
                  key={problem}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="flex items-center gap-4"
                >
                  <div className="w-8 h-8 rounded-full bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
                    <X className="w-4 h-4 text-cyan-400" />
                  </div>
                  <p className="text-white/90 text-lg">{problem}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Solution Side */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-gradient-to-br from-primary to-primary/90 p-8 lg:p-12"
          >
            <div className="flex items-center gap-3 mb-8">
              <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                <Rocket className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-heading font-bold text-2xl lg:text-3xl text-white">
                Ihre Lösung: SR-Zerspanung
              </h3>
            </div>

            <div className="space-y-6">
              {solutions.map((solution, index) => (
                <motion.div
                  key={solution.title}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="flex items-start gap-4"
                >
                  <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <p className="text-white font-semibold text-lg">{solution.title}</p>
                    <p className="text-white/70">{solution.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ProblemSolutionSection;
